import React from 'react';
import { Link } from 'react-router-dom';
import GameCTA from '../../components/GameCTA';

const Kensley: React.FC = () => {
  return (
    <div className="min-h-screen bg-tal-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <Link to="/games" className="text-tal-blue-600 hover:text-tal-blue-700 flex items-center gap-2 font-medium transition-colors">
            ← Back to Games
          </Link>
        </div>

        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-tal-brown-500 to-tal-brown-700 px-8 py-16 text-white text-center">
            <span className="text-8xl mb-4 block">⚔️</span>
            <h1 className="text-5xl font-bold mb-4">Kensley</h1>
            <p className="text-xl opacity-90">Forge Your Legend</p>
            <div className="mt-6">
              <span className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">
                Planned
              </span>
            </div>
          </div>

          <div className="px-8 py-10 space-y-8">
            {/* Overview */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                Overview
              </h2>
              <p className="text-tal-slate-700 leading-relaxed text-lg">
                Kensley is an epic narrative RPG set in a richly detailed fantasy world where ancient magic meets
                political intrigue. As a rising hero in the kingdom of Kensley, your choices will determine not only
                your fate but the future of an entire realm teetering on the brink of war.
              </p>
            </section>

            {/* World */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                The World of Kensley
              </h2>
              <p className="text-tal-slate-700 leading-relaxed mb-4">
                The kingdom of Kensley has stood for a thousand years, a beacon of civilization in a world still
                haunted by the echoes of the Age of Chaos. Built upon the ruins of an ancient empire, the kingdom
                is a tapestry of diverse cultures, each contributing their unique traditions to the realm's strength.
              </p>
              <p className="text-tal-slate-700 leading-relaxed mb-4">
                But beneath the surface of prosperity, old tensions simmer. The noble houses vie for power, the
                merchants' guilds grow wealthy beyond measure, and in the shadows, whispers of a forgotten prophecy
                begin to spread. Meanwhile, at the borders, strange creatures emerge from the Wildlands—creatures
                that shouldn't exist according to any known lore.
              </p>
              <p className="text-tal-slate-700 leading-relaxed">
                You arrive in the capital during the Festival of Lights, a celebration of peace and unity. But
                when a shocking event disrupts the festivities, you find yourself drawn into a web of conspiracy
                that will test your courage, your wit, and your very soul.
              </p>
            </section>

            {/* Key Features */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                Key Features
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-tal-brown-50 p-6 rounded-lg border-l-4 border-tal-brown-500">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">🗺️ Expansive World</h3>
                  <p className="text-tal-slate-700">
                    Explore diverse regions from the bustling capital to mysterious forests, ancient ruins, and
                    treacherous mountain passes. Every location has its own stories to tell.
                  </p>
                </div>
                <div className="bg-tal-brown-50 p-6 rounded-lg border-l-4 border-tal-brown-500">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">⚔️ Strategic Combat</h3>
                  <p className="text-tal-slate-700">
                    Engage in tactical battles where positioning, timing, and strategy matter. Choose your fighting
                    style and develop your skills as you face increasingly challenging foes.
                  </p>
                </div>
                <div className="bg-tal-brown-50 p-6 rounded-lg border-l-4 border-tal-brown-500">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">👥 Companion System</h3>
                  <p className="text-tal-slate-700">
                    Recruit allies from all walks of life. Each companion has their own questline, personality, and
                    abilities that complement your playstyle.
                  </p>
                </div>
                <div className="bg-tal-brown-50 p-6 rounded-lg border-l-4 border-tal-brown-500">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">🏰 Political Intrigue</h3>
                  <p className="text-tal-slate-700">
                    Navigate the dangerous waters of court politics. Form alliances, make enemies, and shape the
                    future of the kingdom through diplomacy or force.
                  </p>
                </div>
              </div>
            </section>

            {/* Character Classes */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                Choose Your Path
              </h2>
              <div className="space-y-4">
                <div className="bg-tal-slate-50 p-5 rounded-lg">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2 flex items-center gap-2">
                    <span>⚔️</span> The Warrior
                  </h3>
                  <p className="text-tal-slate-700 leading-relaxed">
                    Masters of martial combat who lead from the front. Warriors excel at melee combat and can turn
                    the tide of battle through sheer prowess and tactical positioning.
                  </p>
                </div>
                <div className="bg-tal-slate-50 p-5 rounded-lg">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2 flex items-center gap-2">
                    <span>🔮</span> The Mage
                  </h3>
                  <p className="text-tal-slate-700 leading-relaxed">
                    Wielders of arcane power who reshape reality itself. Mages command the elements and can devastate
                    enemies from afar while providing crucial support to allies.
                  </p>
                </div>
                <div className="bg-tal-slate-50 p-5 rounded-lg">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2 flex items-center gap-2">
                    <span>🏹</span> The Ranger
                  </h3>
                  <p className="text-tal-slate-700 leading-relaxed">
                    Swift and deadly marksmen who strike from the shadows. Rangers excel at reconnaissance, archery,
                    and survival in the wild places of the world.
                  </p>
                </div>
                <div className="bg-tal-slate-50 p-5 rounded-lg">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2 flex items-center gap-2">
                    <span>🗡️</span> The Rogue
                  </h3>
                  <p className="text-tal-slate-700 leading-relaxed">
                    Cunning operatives who operate in the shadows. Rogues specialize in stealth, deception, and
                    finding solutions that others might overlook.
                  </p>
                </div>
              </div>
            </section>

            {/* Story Pillars */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                Story Pillars
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-white border-2 border-tal-brown-200 rounded-lg">
                  <div className="text-4xl mb-3">👑</div>
                  <h3 className="text-lg font-semibold text-tal-slate-800 mb-2">Power & Responsibility</h3>
                  <p className="text-sm text-tal-slate-600">
                    What does it mean to lead? How do you wield power justly in an unjust world?
                  </p>
                </div>
                <div className="text-center p-6 bg-white border-2 border-tal-brown-200 rounded-lg">
                  <div className="text-4xl mb-3">⚖️</div>
                  <h3 className="text-lg font-semibold text-tal-slate-800 mb-2">Justice & Mercy</h3>
                  <p className="text-sm text-tal-slate-600">
                    Navigate the gray areas between right and wrong, punishment and forgiveness.
                  </p>
                </div>
                <div className="text-center p-6 bg-white border-2 border-tal-brown-200 rounded-lg">
                  <div className="text-4xl mb-3">🤝</div>
                  <h3 className="text-lg font-semibold text-tal-slate-800 mb-2">Unity & Division</h3>
                  <p className="text-sm text-tal-slate-600">
                    Can a divided kingdom stand? Will you unite the realm or reshape it entirely?
                  </p>
                </div>
              </div>
            </section>

            {/* Design Philosophy */}
            <section className="bg-tal-brown-50 rounded-lg p-6 border-l-4 border-tal-brown-500">
              <h2 className="text-2xl font-bold text-tal-slate-900 mb-4">Our Vision</h2>
              <p className="text-tal-slate-700 leading-relaxed mb-4">
                Kensley is being designed as a deep, meaningful RPG that respects your time and intelligence. Every
                choice matters, every character has depth, and every quest contributes to the larger narrative tapestry.
              </p>
              <p className="text-tal-slate-700 leading-relaxed mb-4">
                We're drawing inspiration from classic CRPGs while incorporating modern quality-of-life features and
                storytelling techniques. This is a game for those who want to lose themselves in a rich fantasy world
                where their decisions truly shape the outcome.
              </p>
              <p className="text-tal-slate-700 leading-relaxed">
                Development will begin once our current projects are further along. We believe in giving each game
                the time and attention it deserves to reach its full potential.
              </p>
            </section>

            {/* Stay Updated */}
            <GameCTA
              title="Join the Adventure"
              description="Stay informed about Kensley's development. We'll share concept art, lore, and development updates as we bring this epic tale to life."
              gradientFrom="from-tal-brown-500"
              gradientTo="to-tal-brown-700"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Kensley;
